import React, { useState } from 'react';
import { Gift, Users, CheckCircle, ArrowRight, Phone, User, Loader2, Star } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReferralPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  // Step 1: Referrer details
  const [referrerData, setReferrerData] = useState({
    name: '',
    mobile: '',
    otp: '',
    googleReviewConfirmed: false
  });

  // Step 2: Referral details
  const [referrals, setReferrals] = useState([
    { name: '', mobile: '' }
  ]);

  const handleReferrerChange = (e) => {
    const { name, value } = e.target;
    setReferrerData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleReferralChange = (index, field, value) => {
    const updated = [...referrals];
    updated[index][field] = value;
    setReferrals(updated);
    setError(null);
  };

  const addReferral = () => {
    setReferrals([...referrals, { name: '', mobile: '' }]);
  };

  const removeReferral = (index) => {
    if (referrals.length > 1) {
      setReferrals(referrals.filter((_, i) => i !== index));
    }
  };

  const sendOtp = async () => {
    if (!referrerData.mobile || referrerData.mobile.length < 10) {
      setError('Please enter a valid mobile number');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/referral/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: referrerData.mobile })
      });

      const data = await response.json();

      if (response.ok) {
        setOtpSent(true);
      } else {
        setError(data.detail || 'Failed to send OTP');
      }
    } catch (err) {
      setError('Failed to send OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!referrerData.otp || referrerData.otp.length < 4) {
      setError('Please enter the OTP');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/referral/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          mobile: referrerData.mobile,
          otp: referrerData.otp 
        })
      });

      const data = await response.json();

      if (response.ok && data.verified) {
        setOtpVerified(true);
      } else {
        setError(data.detail || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      setError('Failed to verify OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const goToStep2 = () => {
    if (!referrerData.name) {
      setError('Please enter your name');
      return;
    }
    if (!otpVerified) {
      setError('Please verify your mobile number first');
      return;
    }
    if (!referrerData.googleReviewConfirmed) {
      setError('Please confirm that you have left a Google review');
      return;
    }
    setCurrentStep(2);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate referrals
    const validReferrals = referrals.filter(r => r.name && r.mobile);
    if (validReferrals.length === 0) {
      setError('Please add at least one referral with name and mobile number');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/referral/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          referrer: {
            name: referrerData.name,
            mobile: referrerData.mobile
          },
          referrals: validReferrals
        })
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to submit referral');
      }
    } catch (err) {
      setError('Failed to submit. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setCurrentStep(1);
    setOtpSent(false);
    setOtpVerified(false);
    setReferrerData({ name: '', mobile: '', otp: '', googleReviewConfirmed: false });
    setReferrals([{ name: '', mobile: '' }]);
  };

  const howItWorks = [
    {
      step: 1,
      title: 'Leave a Review',
      description: 'Before submitting referrals, please leave a positive review of your Supa Energy Hub experience on Google.',
      icon: Star
    },
    {
      step: 2,
      title: 'Submit Your Referral',
      description: 'Fill out the form with your details and the contact information of the person you are referring. Make sure to get their consent.',
      icon: Users
    },
    {
      step: 3,
      title: 'Earn Your Reward',
      description: 'You will receive a $200 gift card once the referred customer\'s installation is completed and fully paid for.',
      icon: Gift
    }
  ];

  // Form Component
  const ReferralForm = () => (
    <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
      {/* Progress Bar */}
      <div className="bg-[#3D5A47] px-5 py-3">
        <div className="flex items-center justify-between text-white mb-2">
          <span className="font-semibold text-sm">Referral Program</span>
          <span className="text-xs">Step {submitted ? '✓' : currentStep} of 2</span>
        </div>
        <div className="w-full bg-white/20 rounded-full h-1.5">
          <div 
            className="bg-[#B5D43B] h-1.5 rounded-full transition-all duration-300"
            style={{ width: submitted ? '100%' : currentStep === 1 ? '50%' : '100%' }}
          />
        </div>
      </div>

      <div className="p-5">
        {submitted ? (
          /* Success State */
          <div className="text-center py-6">
            <div className="w-16 h-16 rounded-full bg-[#B5D43B]/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#3D5A47]" />
            </div>
            <h3 className="text-xl font-bold text-[#3D5A47] mb-3">Thank You!</h3>
            <p className="text-gray-600 text-sm mb-3 leading-relaxed">
              We truly appreciate you spreading the word about Supa Energy Hub! 
            </p>
            <p className="text-gray-600 text-sm mb-3 leading-relaxed">
              Our team will reach out to your referrals and endeavour to deliver the same 
              excellent service and high savings you've enjoyed.
            </p>
            <p className="text-gray-600 text-sm mb-4 leading-relaxed">
              We'll keep you updated. Your <span className="font-semibold text-[#3D5A47]">$200 gift card</span> will be on its way once installation is complete!
            </p>
            <Button onClick={resetForm} className="btn-primary text-sm">
              Submit Another Referral
            </Button>
          </div>
        ) : currentStep === 1 ? (
          /* Step 1: Referrer Details */
          <div>
            <h3 className="font-bold text-[#3D5A47] mb-4">Step 1: Your Details</h3>
            
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-sm">Your Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={referrerData.name}
                  onChange={handleReferrerChange}
                  placeholder="Enter your full name"
                  className="h-10"
                  data-testid="referrer-name-input"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="mobile" className="text-sm">Your Mobile *</Label>
                <div className="flex gap-2">
                  <div className="flex items-center gap-1.5 px-2 bg-gray-100 rounded-lg border border-gray-200 text-sm">
                    <span>🇦🇺</span>
                    <span className="text-gray-600">+61</span>
                  </div>
                  <Input
                    id="mobile"
                    name="mobile"
                    type="tel"
                    value={referrerData.mobile}
                    onChange={handleReferrerChange}
                    placeholder="400 000 000"
                    className="h-10 flex-1"
                    disabled={otpVerified}
                    data-testid="referrer-mobile-input"
                  />
                  {!otpVerified && (
                    <Button
                      type="button"
                      onClick={sendOtp}
                      disabled={isLoading || otpSent}
                      className="btn-primary h-10 px-3 text-sm"
                      data-testid="send-otp-btn"
                    >
                      {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : otpSent ? 'Sent' : 'Send OTP'}
                    </Button>
                  )}
                </div>
                {otpVerified && (
                  <div className="flex items-center gap-1.5 text-green-600 text-xs">
                    <CheckCircle className="w-3.5 h-3.5" />
                    <span>Mobile verified</span>
                  </div>
                )}
              </div>

              {otpSent && !otpVerified && (
                <div className="space-y-1.5">
                  <Label htmlFor="otp" className="text-sm">Enter OTP *</Label>
                  <div className="flex gap-2">
                    <Input
                      id="otp"
                      name="otp"
                      type="text"
                      value={referrerData.otp}
                      onChange={handleReferrerChange}
                      placeholder="Enter 6-digit OTP"
                      className="h-10 flex-1"
                      maxLength={6}
                      data-testid="otp-input"
                    />
                    <Button
                      type="button"
                      onClick={verifyOtp}
                      disabled={isLoading}
                      className="btn-primary h-10 px-4 text-sm"
                      data-testid="verify-otp-btn"
                    >
                      {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">
                    A 6-digit code has been sent to your mobile via SMS.
                  </p>
                </div>
              )}

              <div className="flex items-start gap-2.5 p-3 bg-gray-50 rounded-xl">
                <Checkbox
                  id="googleReview"
                  checked={referrerData.googleReviewConfirmed}
                  onCheckedChange={(checked) => 
                    setReferrerData(prev => ({ ...prev, googleReviewConfirmed: checked }))
                  }
                  data-testid="google-review-checkbox"
                />
                <Label htmlFor="googleReview" className="text-xs leading-relaxed cursor-pointer">
                  I confirm that I have left a positive review on Google. *
                </Label>
              </div>

              {error && (
                <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs">
                  {error}
                </div>
              )}

              <Button
                type="button"
                onClick={goToStep2}
                className="btn-primary w-full gap-2 text-sm"
                data-testid="next-step-btn"
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ) : (
          /* Step 2: Referral Details */
          <form onSubmit={handleSubmit}>
            <h3 className="font-bold text-[#3D5A47] mb-1">Step 2: Referral Details</h3>
            <p className="text-gray-600 text-xs mb-4">
              Share details of people you'd like to refer (with their consent).
            </p>

            <div className="space-y-3">
              {referrals.map((referral, index) => (
                <div key={index} className="p-3 bg-gray-50 rounded-xl space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-[#3D5A47]">
                      Referral {index + 1}
                    </span>
                    {referrals.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeReferral(index)}
                        className="text-red-500 text-xs hover:underline"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Name *</Label>
                      <div className="relative">
                        <User className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          value={referral.name}
                          onChange={(e) => handleReferralChange(index, 'name', e.target.value)}
                          placeholder="Full name"
                          className="h-10 pl-9 text-sm"
                          data-testid={`referral-name-${index}`}
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Mobile *</Label>
                      <div className="relative">
                        <Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          type="tel"
                          value={referral.mobile}
                          onChange={(e) => handleReferralChange(index, 'mobile', e.target.value)}
                          placeholder="0400 000 000"
                          className="h-10 pl-9 text-sm"
                          data-testid={`referral-mobile-${index}`}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={addReferral}
                className="w-full py-2.5 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 text-sm hover:border-[#B5D43B] hover:text-[#3D5A47] transition-colors"
              >
                + Add Another Referral
              </button>

              {error && (
                <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs">
                  {error}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  onClick={() => setCurrentStep(1)}
                  className="btn-secondary flex-1 text-sm"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary flex-1 gap-2 text-sm"
                  data-testid="submit-referral-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit'
                  )}
                </Button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main>
        {/* Hero Section with Form */}
        <section className="pt-28 pb-16 bg-gradient-to-br from-[#f8faf5] via-white to-[#f0f7e6] relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-20 right-10 w-72 h-72 bg-[#B5D43B]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#3D5A47]/5 rounded-full blur-3xl" />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left: Content */}
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#B5D43B]/15 rounded-full mb-6">
                  <Gift className="w-5 h-5 text-[#B5D43B]" />
                  <span className="text-sm font-semibold text-[#3D5A47]">Referral Program</span>
                </div>
                <h1 className="heading-1 mb-6">
                  Help Others Go <span className="text-[#B5D43B]">Supa</span> and Earn{' '}
                  <span className="text-[#B5D43B]">$200!</span>
                </h1>
                <p className="body-large text-gray-600 mb-8">
                  At Supa Energy Hub, we believe in the power of sharing the benefits of clean, renewable energy. 
                  Refer your friends, family, and colleagues to join our community and enjoy the same savings 
                  and environmental benefits you do.
                </p>
                
                {/* Quick Stats */}
                <div className="flex flex-wrap gap-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#B5D43B]/15 flex items-center justify-center">
                      <Gift className="w-6 h-6 text-[#3D5A47]" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#3D5A47]">$200</p>
                      <p className="text-sm text-gray-500">Per Referral</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#B5D43B]/15 flex items-center justify-center">
                      <Users className="w-6 h-6 text-[#3D5A47]" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#3D5A47]">Unlimited</p>
                      <p className="text-sm text-gray-500">Referrals</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Form */}
              <div className="lg:pl-8">
                <ReferralForm />
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="heading-2 text-center mb-12">How It Works</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {howItWorks.map((item) => (
                <div key={item.step} className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-[#B5D43B]/15 flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-8 h-8 text-[#3D5A47]" />
                  </div>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#3D5A47] text-white text-sm font-bold mb-3">
                    {item.step}
                  </div>
                  <h3 className="heading-3 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Terms and Conditions */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="heading-2 text-center mb-12">Terms and Conditions</h2>
            
            <div className="space-y-4">
              <div className="p-5 bg-white rounded-xl border border-gray-100">
                <h4 className="font-semibold text-[#3D5A47] mb-2">1. Eligibility</h4>
                <p className="text-gray-600 text-sm">
                  The Supa Energy Hub Referral Program is open to all existing customers who have had a system installed by us.
                </p>
              </div>

              <div className="p-5 bg-white rounded-xl border border-gray-100">
                <h4 className="font-semibold text-[#3D5A47] mb-2">2. Referral Reward</h4>
                <p className="text-gray-600 text-sm">
                  Referrers will receive a $200 gift card for each successful referral. A referral is considered successful when the referred customer's installation is completed and fully paid for.
                </p>
              </div>

              <div className="p-5 bg-white rounded-xl border border-gray-100">
                <h4 className="font-semibold text-[#3D5A47] mb-2">3. Referral Submission</h4>
                <p className="text-gray-600 text-sm">
                  Before submitting referrals, customers are required to leave a positive review of their Supa Energy Hub experience on Google. Referrals must be submitted through our designated referral form. The referral must include the referred person's name, contact information, and consent to be contacted by Supa Energy Hub.
                </p>
              </div>

              <div className="p-5 bg-white rounded-xl border border-gray-100">
                <h4 className="font-semibold text-[#3D5A47] mb-2">4. Limitations</h4>
                <p className="text-gray-600 text-sm">
                  There is no limit to the number of referrals a customer can make. However, the referral reward is only applicable if the referred customer is a new lead and not already in our database.
                </p>
              </div>

              <div className="p-5 bg-white rounded-xl border border-gray-100">
                <h4 className="font-semibold text-[#3D5A47] mb-2">5. Reward Distribution</h4>
                <p className="text-gray-600 text-sm">
                  The $200 gift card will be issued within 30 days of the successful completion and payment of the referred customer's installation.
                </p>
              </div>

              <div className="p-5 bg-white rounded-xl border border-gray-100">
                <h4 className="font-semibold text-[#3D5A47] mb-2">6. Program Changes</h4>
                <p className="text-gray-600 text-sm">
                  Supa Energy Hub reserves the right to modify, suspend, or terminate the Referral Program at any time without prior notice. Changes will be communicated via our website.
                </p>
              </div>

              <div className="p-5 bg-white rounded-xl border border-gray-100">
                <h4 className="font-semibold text-[#3D5A47] mb-2">7. Non-Transferable</h4>
                <p className="text-gray-600 text-sm">
                  Referral rewards are non-transferable and cannot be exchanged for cash or other products/services.
                </p>
              </div>

              <div className="p-5 bg-white rounded-xl border border-gray-100">
                <h4 className="font-semibold text-[#3D5A47] mb-2">8. Tax Implications</h4>
                <p className="text-gray-600 text-sm">
                  Referral rewards may be subject to tax reporting and withholding. Referrers are responsible for any applicable taxes on referral rewards.
                </p>
              </div>

              <div className="p-5 bg-white rounded-xl border border-gray-100">
                <h4 className="font-semibold text-[#3D5A47] mb-2">9. Fair Use</h4>
                <p className="text-gray-600 text-sm">
                  Referrals must be made in good faith. Any abuse of the Referral Program, including but not limited to spamming, false referrals, or fraudulent activity, will result in disqualification from the program and forfeiture of any rewards earned.
                </p>
              </div>

              <div className="p-5 bg-white rounded-xl border border-gray-100">
                <h4 className="font-semibold text-[#3D5A47] mb-2">10. Disputes</h4>
                <p className="text-gray-600 text-sm">
                  Any disputes regarding the Referral Program will be resolved at the sole discretion of Supa Energy Hub. Our decision will be final and binding.
                </p>
              </div>

              <div className="p-5 bg-white rounded-xl border border-gray-100">
                <h4 className="font-semibold text-[#3D5A47] mb-2">11. Contact Information</h4>
                <p className="text-gray-600 text-sm">
                  For any questions or inquiries about the Referral Program, please contact our team at{' '}
                  <a href="mailto:hello@supaenergyhub.com.au" className="text-[#B5D43B] hover:underline">
                    hello@supaenergyhub.com.au
                  </a>
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ReferralPage;
